import { get } from "svelte/store"
import {
  adminResources,
  type OrganizationMemberIdentityResponse,
} from "@kontextwerk/control-web-client"
import {
  captureControlContext,
  controlContext,
} from "@kontextwerk/control-web-frame/shared/logic/authentication"
import { getAdminApi, getControlWebClient, request, requestBlobWithHeaders } from "../client"
import { invalidateOrganizationCaches } from "../organizations"

export type DemoDataResetAudience = "admin" | "employee"

export type DemoDataResetMembershipSelection = {
  membership_id: string
  membership_revision: number
}

export type DemoDataResetRequest = {
  primary: DemoDataResetMembershipSelection
  secondary: DemoDataResetMembershipSelection
  base_date: string | null
  reset_settings: boolean
  target_plan_family: "kehrbase" | "kehrchat" | "kehrtalk" | null
  calendar_projection: boolean
}

export type DemoDataResetResponse = {
  demo_reset_id: string
  base_date: string
  primary_membership_id: string
  secondary_membership_id: string
  counts: Record<string, number>
  calendar_sync_status: "skipped" | "queued" | "synced"
  warnings: string[]
}

export type DemoDataResetMembershipCandidate = {
  membership_id: string
  membership_revision: number
  user_id: string
  display_name: string
  username: string
}

export type DemoDataResetJobStatus = "queued" | "running" | "succeeded" | "failed"
export type DemoDataResetPhase =
  | "validating"
  | "configuring"
  | "cleaning"
  | "creating_users"
  | "creating_registry"
  | "creating_work_orders"
  | "creating_schedule"
  | "creating_communications"
  | "finalizing"

export type DemoDataResetJobError = {
  code: string
  message: string
}

export type DemoDataResetJobView = {
  job_id: string
  organization_id: string
  source: "manual" | "cli" | "weekly" | "admin_quick"
  status: DemoDataResetJobStatus
  phase: DemoDataResetPhase | null
  completed_steps: number
  total_steps: number
  progress_percent: number
  attempt: number
  poll_after_ms: number
  created_at: string
  updated_at: string
  started_at: string | null
  finished_at: string | null
  next_attempt_at: string | null
  result: DemoDataResetResponse | null
  error: DemoDataResetJobError | null
}

export type DemoDataResetJobStartResponse = {
  job_id: string
  organization_id: string
  enqueue_disposition: "created" | "idempotent_replay"
}

export type DemoDataResetStreetListDownload = {
  blob: Blob
  contentDisposition: string | null
}

const MEMBERSHIP_PAGE_LIMIT = 100

const memberDisplayName = (member: OrganizationMemberIdentityResponse): string =>
  [member.first_name, member.last_name]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(" ") || member.username

/**
 * Active members of the admitted organization with their current membership revision. A reset
 * selects two of them; their identities, roles and credentials stay unchanged.
 */
export const listDemoResetMembershipCandidates = async (
  organizationId: string,
  options: { signal: AbortSignal },
): Promise<DemoDataResetMembershipCandidate[]> => {
  const capture = captureControlContext()

  if (!capture || get(controlContext)?.organizationId !== organizationId)
    throw new DOMException("Unconfirmed demo reset roster context", "AbortError")

  const signal = AbortSignal.any([capture.signal, options.signal])
  const current = () => {
    if (signal.aborted || !capture.isCurrent())
      throw new DOMException("Obsolete demo reset roster", "AbortError")
  }
  const frame = await getAdminApi().organization.registry.getFrame(organizationId, {
    signal,
    isCurrent: capture.isCurrent,
  })
  current()
  const members = new Map(
    (frame.users ?? [])
      .filter((member) => member.organization_id === organizationId)
      .map((member) => [member.membership_id, member]),
  )
  const api = new adminResources.AdminIdentityAdministrationAPI(getControlWebClient().transport)
  const roster = { signal, isCurrent: capture.isCurrent, mutationContext: capture.mutationContext }
  const candidates: DemoDataResetMembershipCandidate[] = []
  const cursors = new Set<string>()
  let after: string | null = null
  do {
    const page = await api.memberships(
      organizationId,
      { limit: MEMBERSHIP_PAGE_LIMIT, after_id: after },
      roster,
    )
    current()
    for (const membership of page.items) {
      const member = members.get(membership.id)

      if (membership.status !== "active" || member?.id !== membership.user_id) continue

      candidates.push({
        membership_id: membership.id,
        membership_revision: membership.revision,
        user_id: membership.user_id,
        display_name: memberDisplayName(member),
        username: member.username,
      })
    }
    after = page.next_after_id

    if (after && (cursors.has(after) || !page.items.length))
      throw new TypeError("Demo reset roster cursor did not advance")

    if (after) cursors.add(after)
  } while (after)

  return candidates
}

export const enqueueDemoOrganizationResetJob = (
  organizationId: string,
  payload: DemoDataResetRequest,
  options: { idempotencyKey: string; signal?: AbortSignal },
): Promise<DemoDataResetJobStartResponse> =>
  request<DemoDataResetJobStartResponse>(`/organizations/${organizationId}/demo-reset-jobs`, {
    method: "POST",
    headers: { "Idempotency-Key": options.idempotencyKey },
    body: JSON.stringify(payload),
    signal: options.signal,
  })

export const getDemoOrganizationResetJob = (
  organizationId: string,
  jobId: string,
  options: { signal?: AbortSignal } = {},
): Promise<DemoDataResetJobView> =>
  request<DemoDataResetJobView>(`/organizations/${organizationId}/demo-reset-jobs/${jobId}`, {
    method: "GET",
    signal: options.signal,
  })

export const downloadDemoOrganizationResetStreetList = async (
  organizationId: string,
  jobId: string,
  audience: DemoDataResetAudience,
  options: { signal?: AbortSignal } = {},
): Promise<DemoDataResetStreetListDownload> => {
  const response = await requestBlobWithHeaders(
    `/organizations/${organizationId}/demo-reset-jobs/${jobId}/street-lists/${audience}/download`,
    {
      method: "GET",
      signal: options.signal,
    },
  )

  return {
    blob: response.data,
    contentDisposition: response.headers.get("content-disposition"),
  }
}

export const completeDemoOrganizationReset = (
  organizationId: string,
  result: DemoDataResetResponse,
): DemoDataResetResponse => {
  invalidateOrganizationCaches(organizationId)

  return result
}
